// The account feature's server state: profile (with its ETag) and preferences on the web API.
import { ProblemError } from "@cosmosai/api-client";
import { MeExport, Preferences, type PreferencesPatch, Profile } from "@cosmosai/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { type AppFetch, useAppFetch } from "../../api/client";
import { toast } from "../../ui/sonner";
import { useSignOut } from "../auth/auth-context";
import { applyPreferences, forgetLocalPreferences, withPatch } from "./apply";

export const profileKey = ["me", "profile"] as const;
export const preferencesKey = ["me", "preferences"] as const;

/** The profile and the ETag of the version it was read at. */
export interface ProfileState {
  profile: Profile;
  etag: string;
}

/** The preferences, and whether the user ever saved them (else they are the defaults). */
export interface PreferencesState {
  value: Preferences;
  saved: boolean;
}

async function readProfile(appFetch: AppFetch, init?: RequestInit): Promise<ProfileState> {
  const { body, headers } = await appFetch.withHeaders<unknown>("/me/profile", init);
  return { profile: Profile.parse(body), etag: headers.get("etag") ?? "" };
}

export function useProfile() {
  const appFetch = useAppFetch();
  return useQuery({ queryKey: profileKey, queryFn: () => readProfile(appFetch) });
}

/** Saves the profile read at `etag`; a 412 means it changed elsewhere (see isChangedElsewhere). */
export function useSaveProfile() {
  const appFetch = useAppFetch();
  const queries = useQueryClient();
  return useMutation({
    mutationFn: ({ profile, etag }: ProfileState) =>
      readProfile(appFetch, {
        method: "PUT",
        body: JSON.stringify(profile),
        headers: { "if-match": etag },
      }),
    onSuccess: (saved) => queries.setQueryData(profileKey, saved),
  });
}

export const isChangedElsewhere = (error: unknown): boolean =>
  error instanceof ProblemError && error.status === 412;

/** The saved preferences with their "saved" flag: the web API sends Last-Modified once saved. */
export function usePreferencesState() {
  const appFetch = useAppFetch();
  return useQuery({
    queryKey: preferencesKey,
    queryFn: async (): Promise<PreferencesState> => {
      const { body, headers } = await appFetch.withHeaders<unknown>("/me/preferences");
      return { value: Preferences.parse(body), saved: headers.has("last-modified") };
    },
  });
}

/** The user's preferences, the same on every device. */
export function usePreferences() {
  const state = usePreferencesState();
  return { ...state, data: state.data?.value };
}

/** Saves some preferences: applied and cached at once, rolled back if the save fails. */
export function useUpdatePreferences() {
  const appFetch = useAppFetch();
  const queries = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationKey: preferencesKey,
    mutationFn: async (patch: PreferencesPatch) =>
      Preferences.parse(
        await appFetch<unknown>("/me/preferences", {
          method: "PATCH",
          body: JSON.stringify(patch),
        }),
      ),
    onMutate: async (patch) => {
      applyPreferences(patch);
      await queries.cancelQueries({ queryKey: preferencesKey });
      const previous = queries.getQueryData<PreferencesState>(preferencesKey);
      if (previous) {
        queries.setQueryData<PreferencesState>(preferencesKey, {
          value: withPatch(previous.value, patch),
          saved: true,
        });
      }
      return { previous };
    },
    onError: (_error, _patch, context) => {
      if (context?.previous) {
        queries.setQueryData(preferencesKey, context.previous);
        applyPreferences(context.previous.value);
      }
      toast.error(t("account.preferences.notSaved"));
    },
    onSuccess: (value) => {
      // A later change still in flight keeps its optimistic value until its own answer.
      if (queries.isMutating({ mutationKey: preferencesKey }) <= 1) {
        queries.setQueryData<PreferencesState>(preferencesKey, { value, saved: true });
      }
    },
  });
}

/** Downloads everything the web side keeps about the user as a JSON file. */
export function useExportMe() {
  const appFetch = useAppFetch();
  return useMutation({
    mutationFn: async () => {
      const data = MeExport.parse(await appFetch<unknown>("/me/export"));
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "cosmosai-web-data.json";
      link.click();
      URL.revokeObjectURL(url);
    },
  });
}

/** Erases the user's web-side data, forgets this browser's caches, then signs out. */
export function useEraseMe() {
  const appFetch = useAppFetch();
  const signOut = useSignOut();
  return useMutation({
    mutationFn: () => appFetch<void>("/me", { method: "DELETE" }),
    onSuccess: async () => {
      forgetLocalPreferences();
      await signOut();
    },
  });
}
